import { useRouter } from 'next/router'
import { useEffect, useMemo } from 'react'

const CLEAR_VALUE = 'CLEAR'

export type ParamValue = string | string[] | undefined
export type Params = { [key: string]: ParamValue }

export const useFilters = ({
  supportedFilters = [],
  initialFilters
}: {
  supportedFilters?: string[]
  /**
   * IMPORTANT: to use this predictably, we need to set
   * hideAllOption: true on the filter, and have a custom
   * option which value is not CLEAR, and handled accordingly
   * in the where builder helper function.
   */
  initialFilters?: Params
}) => {
  const router = useRouter()

  const setQueryParams = (params: Params, preserveValues = false) => {
    Object.entries(params).forEach(
      ([paramKey, paramVal]: [string, ParamValue]) => {
        if (preserveValues && router.query[paramKey]) return
        if ([CLEAR_VALUE, ''].includes(paramVal as string)) {
          delete router.query[paramKey]
        } else if (paramVal) {
          router.query[paramKey] = paramVal
        }
      }
    )
  }

  const applyAllowlistAndStripNullEntries = (values: Params) => {
    return Object.entries(values).reduce(
      (
        obj: { [key: string]: ParamValue },
        [key, val]: [string, ParamValue | null]
      ) => {
        if (supportedFilters.includes(key) && val !== null)
          return { ...obj, [key]: val }
        return obj
      },
      {}
    )
  }

  useEffect(() => {
    if (initialFilters) {
      const params = applyAllowlistAndStripNullEntries(initialFilters)

      setQueryParams(params, true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const filters = useMemo(() => {
    return supportedFilters.reduce(
      (obj: { [key: string]: ParamValue | null }, filter: string) => {
        return {
          ...obj,
          [filter]: router.query[filter] as ParamValue | null
        }
      },
      {}
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.query, supportedFilters])

  const setFilters = (newFilters: Params) => {
    const params = applyAllowlistAndStripNullEntries(newFilters)

    setQueryParams(params)
    router.push({ pathname: router.pathname, query: router.query }, undefined, {
      scroll: false,
      shallow: true
    })
  }

  const clearFilters = () => {
    supportedFilters.forEach((filter: string) => {
      delete router.query[filter]
    })

    router.push({ pathname: router.pathname, query: router.query }, undefined, {
      scroll: false,
      shallow: true
    })
  }

  return { filters, setFilters, clearFilters }
}
